import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MbaIcon, MbaIconName } from '../../../assignment/atoms/mba-icon/mba-icon';
import { SectionStatus } from '../../briefing.helpers';
import { bf } from '../../briefing-ui';

/**
 * One Briefing section card: icon tile, title, status pill, helper text and an Edit button
 * (only once the section is set; unset sections offer Set up inside their body).
 */
@Component({
  selector: 'mbt-briefing-section',
  imports: [MbaIcon],
  template: `
    <section [id]="'bf-' + key()" [class]="cardClass()" [attr.aria-labelledby]="'bf-' + key() + '-title'">
      <div class="flex flex-wrap items-start gap-3.5 px-5 pt-[18px]">
        <span [class]="status() === 'set' ? bf.iconTile : bf.iconTileAmber"><mba-icon [name]="icon()" size="size-5" /></span>
        <span class="flex min-w-0 flex-1 flex-col gap-1.5">
          <span class="flex flex-wrap items-center gap-2.5">
            <h2 [id]="'bf-' + key() + '-title'" [class]="bf.h2">{{ title() }}</h2>
            @switch (status()) {
              @case ('set') { <span [class]="bf.pillSet"><mba-icon name="check" size="size-3" [strokeWidth]="2.5" />Set</span> }
              @case ('weak') { <span [class]="bf.pillAmber"><mba-icon name="alert-triangle" size="size-3" />Needs more detail</span> }
              @default { <span [class]="bf.pillAmber"><mba-icon name="circle-dashed" size="size-3" />Not set</span> }
            }
          </span>
          <span [class]="bf.helper">{{ helper() }}</span>
        </span>
        @if (status() !== 'missing' && editable()) {
          <button type="button" [class]="bf.btnOutline" [attr.aria-label]="'Edit ' + title()" (click)="edit.emit()">
            <mba-icon name="pencil" size="size-4" />Edit
          </button>
        }
      </div>
      <div class="flex flex-col gap-4 px-5 pt-[18px] pb-5">
        <ng-content />
      </div>
    </section>
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingSection {
  protected readonly bf = bf;

  readonly key = input.required<string>();
  readonly icon = input.required<MbaIconName>();
  readonly title = input.required<string>();
  readonly helper = input('');
  readonly status = input<SectionStatus>('set');
  /** False hides Edit (e.g. a section that is read-only for this brief). */
  readonly editable = input(true);
  /** Briefly rings the card after the page scrolls to it. */
  readonly flash = input(false);
  readonly edit = output<void>();

  protected readonly cardClass = computed(() => {
    const base =
      this.status() === 'missing' ? bf.sectionMissing : this.status() === 'weak' ? bf.sectionWeak : bf.section;
    return this.flash() ? `${base} ring-4 ring-(--app-accent-ring)` : base;
  });
}
